import omit from 'lodash/omit';

import { restApiService } from '../services/services';
import { handleAxiosResponse } from '../util/apiHelpers';
import { axiosStorableError } from '../util/errors';

const SAVE_CONFIG_REQUEST = 'app/SavedListings/SAVE_CONFIG_REQUEST';
const SAVE_CONFIG_SUCCESS = 'app/SavedListings/SAVE_CONFIG_SUCCESS';
const SAVE_CONFIG_ERROR = 'app/SavedListings/SAVE_CONFIG_ERROR';
const SAVE_CONFIG_RESET = 'app/SavedListings/SAVE_CONFIG_RESET';

const SAVE_LISTING_IDS_REQUEST = 'app/SavedListings/SAVE_LISTING_IDS_REQUEST';
const SAVE_LISTING_IDS_SUCCESS = 'app/SavedListings/SAVE_LISTING_IDS_SUCCESS';
const SAVE_LISTING_IDS_ERROR = 'app/SavedListings/SAVE_LISTING_IDS_ERROR';

const GET_CONFIG_REQUEST = 'app/SavedListings/GET_CONFIG_REQUEST';
const GET_CONFIG_SUCCESS = 'app/SavedListings/GET_CONFIG_SUCCESS';
const GET_CONFIG_ERROR = 'app/SavedListings/GET_CONFIG_ERROR';

const initialSaveConfigState = {
  saveConfigInProgress: false,
  saveConfigError: null,
  saveConfigSuccessText: null,
};

const initialState = {
  saveInProgress: false,
  saveError: null,
  getConfigInProgress: false,
  getConfigError: null,
  config: null,
  listingIds: [],
  ...initialSaveConfigState,
};

const savedListingsReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SAVE_LISTING_IDS_REQUEST:
      return {
        ...state,
        saveInProgress: true,
        saveError: null,
      };

    case SAVE_LISTING_IDS_SUCCESS:
      return {
        ...state,
        saveInProgress: false,
        listingIds: payload,
      };

    case SAVE_LISTING_IDS_ERROR:
      return {
        ...state,
        saveInProgress: false,
        saveError: payload,
      };

    case GET_CONFIG_REQUEST:
      return {
        ...state,
        getConfigInProgress: true,
        getConfigError: null,
      };

    case GET_CONFIG_SUCCESS:
      return {
        ...state,
        getConfigInProgress: false,
        config: payload.config,
        listingIds: payload.listingIds,
      };

    case GET_CONFIG_ERROR:
      return {
        ...state,
        getConfigInProgress: false,
        getConfigError: payload,
      };

    case SAVE_CONFIG_REQUEST:
      return {
        ...state,
        saveConfigInProgress: true,
        saveConfigSuccessText: null,
        saveConfigError: null,
      };

    case SAVE_CONFIG_SUCCESS:
      return {
        ...state,
        saveConfigInProgress: false,
        config: payload.config,
        saveConfigSuccessText: payload.successText,
      };

    case SAVE_CONFIG_ERROR:
      return {
        ...state,
        saveConfigInProgress: false,
        saveConfigError: payload,
      };

    case SAVE_CONFIG_RESET:
      return {
        ...state,
        ...initialSaveConfigState,
      };

    default:
      return state;
  }
};

export default savedListingsReducer;

const saveListingIdsRequest = () => ({
  type: SAVE_LISTING_IDS_REQUEST,
});

const saveListingIdsSuccess = (listingIds) => ({
  type: SAVE_LISTING_IDS_SUCCESS,
  payload: listingIds,
});

const saveListingIdsError = error => ({
  type: SAVE_LISTING_IDS_ERROR,
  payload: error,
});

const getConfigRequest = () => ({
  type: GET_CONFIG_REQUEST,
});

const getConfigSuccess = (config, listingIds) => ({
  type: GET_CONFIG_SUCCESS,
  payload: {
    config,
    listingIds,
  },
});

const getConfigError = error => ({
  type: GET_CONFIG_ERROR,
  payload: error,
});

const saveConfigRequest = () => ({
  type: SAVE_CONFIG_REQUEST,
});

const saveConfigSuccess = (config, successText) => ({
  type: SAVE_CONFIG_SUCCESS,
  payload: { config, successText },
});

const saveConfigError = error => ({
  type: SAVE_CONFIG_ERROR,
  payload: error,
});

const saveConfigResetAction = () => ({
  type: SAVE_CONFIG_RESET,
});

export const $getConfigState = state => {
  const subState = state.SavedListings;
  return {
    inProgress: subState.getConfigInProgress,
    error: subState.getConfigError,
    config: subState.config,
    listingIds: subState.listingIds,
  };
};

export const $saveConfigState = state => {
  const subState = state.SavedListings;
  return {
    inProgress: subState.saveConfigInProgress,
    error: subState.saveConfigError,
    successText: subState.saveConfigSuccessText,
  };
};

export const $saveListingIdsState = state => {
  const subState = state.SavedListings;
  return {
    inProgress: subState.saveInProgress,
    error: subState.saveError,
  };
};

export const getConfig = () => dispatch => {
  dispatch(getConfigRequest());
  return restApiService.secureInstance
    .getRaw(`savedListings/config`)
    .then(handleAxiosResponse)
    .then(resp => {
      const config = omit(resp, ['listingIds']);
      dispatch(getConfigSuccess(config, resp.listingIds));
      return resp;
    })
    .catch(e => {
      dispatch(getConfigError(axiosStorableError(e)));
      throw e;
    })
  ;
};

export const saveConfig = (userId, values) => dispatch => {
  dispatch(saveConfigRequest());
  return restApiService.secureInstance
    .postRaw(`savedListings/${userId}`, {
      freq: values.freq,
      toSms: values.toSms || false,
      toEmail: values.toEmail || false,
    })
    .then(handleAxiosResponse)
    .then(resp => {
      const config = omit(resp, ['listingIds']);
      const successText = (values.toSms || values.toEmail)
        ? "Watchlist notification(s) saved. We'll notify you when a new listing matches your search"
        : "Watchlist notification(s) has been saved"
      ;
      dispatch(saveConfigSuccess(config, successText));
      return resp;
    })
    .catch(e => {
      dispatch(saveConfigError(axiosStorableError(e)));
      throw e;
    })
  ;
};

export const saveListingIds = (lType, authorId, currUserId, listingId) => dispatch => {
  dispatch(saveListingIdsRequest());
  return restApiService.secureInstance
    .postRaw(`savedListings/editListingIds/${currUserId}`, {
      listingId: listingId,
      listingType: lType,
      authorId,
    })
    .then(handleAxiosResponse)
    .then(resp => {
      dispatch(saveListingIdsSuccess(resp));
      return resp;
    })
    .catch(e => {
      dispatch(saveListingIdsError(axiosStorableError(e)));
      throw e;
    })
  ;
};

export const saveConfigReset = () => dispatch => {
  dispatch(saveConfigResetAction());
};
